import React from 'react';
import { graphql, StaticQuery } from 'gatsby';
import { Accordion } from 'react-accessible-accordion';
import { Row, Col } from 'react-flexbox-grid';
import {
  SectionHeader,
  Section,
  FullwidthImage,
  Paragraph,
  AbsoluteCTA,
  UnorderedList,
  Accordion as AccordionCustom,
} from 'components';

const Teams = () => (
  <StaticQuery
    query={graphql`
      query {
        prototypeTeamImage: file(
          relativePath: { eq: "development-teams/dt-proto-team-big.jpg" }
        ) {
          childImageSharp {
            fluid(maxWidth: 1920) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        mvpTeamImage: file(
          relativePath: { eq: "development-teams/dt-mvp-team-big.jpg" }
        ) {
          childImageSharp {
            fluid(maxWidth: 1920) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        fitTeamImage: file(
          relativePath: { eq: "development-teams/dt-fit-team-big.jpg" }
        ) {
          childImageSharp {
            fluid(maxWidth: 1920) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        scaleTeamImage: file(relativePath: { eq: "culture/holaSlider.jpg" }) {
          childImageSharp {
            fluid(maxWidth: 1920) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    `}
    render={data => (
      <>
        <Section color={'pigeon-post'}>
          <SectionHeader
            size={'big'}
            lineColor={'red'}
            headingType={2}
            color={'white'}
            description={
              'Boldare’s dedicated agile dev teams will help you strategize, ideate with prototypes, design and develop software for your MVP, find a product-market fit and scale your web product using a lean approach to product development. <br /><br /> Our teams have all the skills required to build the product and help you reach your business goals:'
            }
            descriptionColor={'black'}
          >
            Dedicated Product Development Teams
          </SectionHeader>
          <UnorderedList
            containerClass={'development-teams__team-list'}
            additionalClass={
              'development-teams__list-item text--default text--white'
            }
            items={[
              'front-end developers',
              'back-end developers',
              'full stack devs',
              'Quality Engineers (QA)',
              'Business Analysts',
              'DevOps',
              'SEO',
              'Scrum Masters',
              'Agile Coaches',
              'Product Vision',
              'Digital Strategists',
              'customer experience specialists',
              'Visual Designers',
              'Interaction Designers',
              'UX Designers',
              'Product Designers',
              'Qualitative/ Quantitative<br /> Design Researchers',
              'Information Architects',
            ]}
          />
        </Section>
        <Accordion>
          <AccordionCustom
            id={'prototyping-team'}
            backgroundColor={'gray-light'}
            headline={'Prototyping Team'}
            lead={'Bridging the gap between ideation and commercial launch'}
            description={
              'The prototyping team will get to know your business goals and help you conceptualize the product by designing and developing low-fidelity or high-fidelity prototypes. They will test the prototypes with users to check basic assumptions about the product and business which will help at the MVP stage. They will advise you on the best course of further product development.'
            }
            lineColor={'red'}
            fontColors={['black', 'black', 'black']}
          >
            <div className={'hide-mobile'}>
              <div className={'w-container'}>
                <div className={'accordion__content'}>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Accountabilities
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            [
                              'Minimizing the risk',
                              ' of failure in the long run.',
                            ],
                            [
                              'Tackling real customer needs,',
                              ' with empathy, in an innovative way.',
                            ],
                            [
                              'Conducting small experiments.',
                              ' Because the experiments are small, the risk is low.',
                            ],
                          ]}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Knowledge
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            [
                              'Prototyping techniques',
                              ' (Design Thinking, Wireframing)',
                            ],
                            ['KISS techniques', ' (Keep it Simple, Stupid)'],
                            ['Business analysis'],
                            ['Testing methods and tools'],
                            ['Design sprints'],
                          ]}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Specific attributes
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            ['Openness', ' to failure and risk.'],
                            ['Quick decision', '-making.'],
                            [
                              'Balancing the measurable',
                              ' and the unmeasurable (emotions, connotations, habits).',
                            ],
                          ]}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Culture
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            [
                              'Design culture - a system where design can thrive - making design everyone’s job.',
                            ],
                            ['A culture of experimentation'],
                            [
                              'Openness to bold ideas',
                              ' and sometimes, big missteps.',
                            ],
                          ]}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Approach
                        </SectionHeader>
                        <Paragraph
                          size={'medium-plus'}
                          additionalClass={
                            'text--blue-light accordion__description'
                          }
                        >
                          Ideate, build rapidly, test, learn, throw out, build
                          again.
                        </Paragraph>
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Technologies
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            'JavaScript, AngularJS, Design tools: InVision, Flinto, Framer.js, HTML, Bootstrap, Foundation',
                          ]}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
              <AbsoluteCTA
                additionalClass={'accordion__CTA'}
                id={'page-development-teams-solution'}
              />
              <FullwidthImage
                image={data.prototypeTeamImage.childImageSharp.fluid}
                alt={''}
              />
            </div>
          </AccordionCustom>

          <AccordionCustom
            id={'mvp-team'}
            backgroundColor={'mine-shaft'}
            headline={'Minimum Viable Product (MVP) Team '}
            lead={
              'Getting the product to market as quickly as possible to gather feedback from users, helping to improve the product or pivot.'
            }
            description={
              'The MVP development team will apply the lessons learned during the prototyping phase and help you select the most important features for the first release of your product. They will also design the product and develop the software for the MVP. This product team will also advise you on what to test and how to select methods to validate your business hypothesis.'
            }
            lineColor={'pigeon-post'}
            fontColors={['white', 'white', 'white']}
          >
            <>
              <div className={'w-container'}>
                <div className={'accordion__content'}>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Accountabilities
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            'Testing a product hypothesis with minimal resources.',
                            'Solving the problem with the most basic solution.',
                            'Designing and developing the first version of the product.',
                            'Gathering the first round of feedback from the customers.',
                          ]}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Specific attributes
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            'Oriented towards achieving business goals.',
                            'Focused on delivering the product to market.',
                            'Always looking for simplest and quickest solutions.',
                          ]}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Knowledge
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            'Business Model Canvas technique',
                            'Product vision',
                            'Product discovery tools and methods',
                            'Impact mapping',
                            'Human-centred design',
                            'Business transformation',
                            'Innovation strategy',
                            'New venture design',
                            'Design sprints',
                          ]}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div
                        className={
                          'accordion__inner development-teams__team-roles'
                        }
                      >
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Team roles
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={
                            'text--medium text--black accordion__list-item'
                          }
                          items={[
                            ['Frontend<br /> Developers', 'computer-screen'],
                            ['Backend<br /> Developers', 'network'],
                            ['Visual<br /> Designer', 'vector'],
                            ['Interaction<br /> Designer', 'mouse'],
                            ['UX Designer', 'artboard'],
                            ['Product<br /> Designer', 'cube'],
                            ['Business<br /> Analyst', 'grid-world'],
                            ['DevOps', 'dashboard'],
                            [
                              'Qualitative/ Quantitative<br /> Design Researcher',
                              'loupe',
                            ],
                            ['Digital<br /> Strategist', 'rocket'],
                            ['Scrum<br />Master', 'rocket'],
                          ]}
                          icon
                          iconColor={'violet'}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Culture
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            'Pro-risk environment.',
                            'Agility.',
                            'Business goals oriented.',
                            'Perfectionism is the biggest enemy.',
                          ]}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Technologies
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            'JavaScript, AngularJS, Vue.js, React, Node.js',
                          ]}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Approach
                        </SectionHeader>
                        <Paragraph
                          size={'medium-plus'}
                          additionalClass={
                            'text--blue-light accordion__description'
                          }
                        >
                          Enter the market with maximum speed and minimum cost.
                        </Paragraph>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
              <AbsoluteCTA
                additionalClass={'accordion__CTA'}
                id={'page-development-teams-solution'}
              />
              <FullwidthImage
                image={data.mvpTeamImage.childImageSharp.fluid}
                alt={''}
              />
            </>
          </AccordionCustom>

          <AccordionCustom
            id={'product-team'}
            backgroundColor={'gray-light'}
            headline={'Product-Market Fit Team'}
            lead={
              'Developing a sellable product, ideally a "must-have" for the market.'
            }
            description={
              'The Product-Market Fit team plans and executes product tests, analyzes the results and draws conclusions, and applies them to product iterations or pivots. The team’s designers and developers will build new product features and test them with users. They will help you to grow an in-house culture of testing and making data-driven product decisions.'
            }
            lineColor={'red'}
            fontColors={['black', 'black', 'black']}
          >
            <>
              <div className={'w-container'}>
                <div className={'accordion__content'}>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Accountabilities
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            'Validated learning: seeking hypotheses, designing and running experiments.',
                            'Interpreting experiment results and advising on the growth engine and product strategy.',
                            'Designing and engineering the product’s technical architecture to be quickly changeable and adaptable.',
                            'Controlling and limiting the technical debt to ready the product for quick scaling.',
                          ]}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Specific attributes
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            'Tech knowledge prioritization.',
                            'Focused on analytics and drawing meaningful conclusions.',
                          ]}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Knowledge
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            'Digital product development',
                            'Software & app development',
                            'Quality assurance',
                            'Product-Market Fit Canvas',
                            'Quantitative research',
                            'Data visualization & reporting',
                            'Data architecture',
                          ]}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div
                        className={
                          'accordion__inner development-teams__team-roles'
                        }
                      >
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Team roles
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={
                            'text--medium text--black accordion__list-item'
                          }
                          items={[
                            ['Frontend<br /> Developers', 'computer-screen'],
                            ['Backend<br /> Developers', 'network'],
                            ['Visual<br /> Designer', 'vector'],
                            ['Interaction<br /> Designer', 'mouse'],
                            ['UX Designer', 'artboard'],
                            ['Product<br /> Designer', 'cube'],
                            ['Business<br /> Analyst', 'grid-world'],
                            ['DevOps', 'dashboard'],
                            ['Quality<br > Engineer', 'star'],
                            ['Digital<br /> Strategist', 'rocket'],
                            ['Scrum<br />Master', 'rocket'],
                            ['Information<br /> Architect', 'maze'],
                            ['Agile Coach', 'light-bulb'],
                            [
                              'Qualitative/ Quantitative<br /> Design Researcher',
                              'loupe',
                            ],
                          ]}
                          icon
                          iconColor={'violet'}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Culture
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            'Culture of experimentation.',
                            'Driven by business needs.',
                          ]}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Technologies
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            '<span class="text--default">Web</span>: Angular, React, Vue.js, JavaScript, HTML',
                            '<span class="text--default">Backend/API</span>: PHP, Java, Node.js, Express.js, Python',
                            '<span class="text--default">Mobile</span>: React-Native, Swift, Objective-C, Android (Java), Xamarin (.NET)',
                            '<span class="text--default">Machine learning</span>: Python',
                            '<span class="text--default">Mobile</span>: MongoDB, Postgres',
                          ]}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Approach
                        </SectionHeader>
                        <Paragraph
                          size={'medium-plus'}
                          additionalClass={
                            'text--blue-light accordion__description'
                          }
                        >
                          Data-driven and tech-savvy agile development.
                        </Paragraph>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
              <AbsoluteCTA
                additionalClass={'accordion__CTA'}
                id={'page-development-teams-solution'}
              />
              <FullwidthImage
                image={data.fitTeamImage.childImageSharp.fluid}
                alt={''}
              />
            </>
          </AccordionCustom>

          <AccordionCustom
            id={'scale-team'}
            backgroundColor={'red'}
            headline={'Scale & Maturity Team'}
            lead={
              'Building a product that can grow with the users and fulfil the business goals.'
            }
            description={
              'The Scale & Maturity development team will engineer the product for scalability and stability. Additionally, they will advise you on how to set up processes in your company that will allow you to grow your product sustainably in the future, avoiding pitfalls such as accumulating technical debt.'
            }
            lineColor={'pigeon-post'}
            fontColors={['white', 'white', 'white']}
          >
            <>
              <div className={'w-container'}>
                <div className={'accordion__content'}>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Accountabilities
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            '<span class="text--default">Horizontal and vertical expansion of the product.</span>',
                            'Engineering the product for scalability.',
                            'Engineering the product for stability.',
                            'Setting up automated regression testing.',
                            'Polishing the existing functionalities.',
                          ]}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Knowledge
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            '<span class="text--default">Digital Product Development</span>',
                            'Software & app development',
                            'Quality assurance',
                            'Scrum',
                            'Kanban',
                          ]}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Technologies
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={[
                            '<span class="text--default">Web</span>: Angular, React, Vue.js, JavaScript, HTML',
                            '<span class="text--default">Backend/API</span>: PHP, Java, Node.js, Express.js, Python',
                            '<span class="text--default">Mobile</span>: React-Native, Swift, Objective-C, Android (Java), Xamarin (.NET)',
                            '<span class="text--default">Machine learning</span>: Python',
                            '<span class="text--default">Mobile</span>: MongoDB, Postgres',
                          ]}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div
                        className={
                          'accordion__inner development-teams__team-roles'
                        }
                      >
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Team roles
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={
                            'text--medium text--black accordion__list-item'
                          }
                          items={[
                            ['Frontend<br /> Developers', 'computer-screen'],
                            ['Backend<br /> Developers', 'network'],
                            ['Visual<br /> Designer', 'vector'],
                            ['Interaction<br /> Designer', 'mouse'],
                            ['UX Designer', 'artboard'],
                            ['Product<br /> Designer', 'cube'],
                            ['Business<br /> Analyst', 'grid-world'],
                            ['DevOps', 'dashboard'],
                            ['Quality<br > Engineer', 'star'],
                            ['Digital<br /> Strategist', 'rocket'],
                            ['Scrum<br />Master', 'rocket'],
                            ['Information<br /> Architect', 'maze'],
                            ['Agile Coach', 'light-bulb'],
                            [
                              'Qualitative/ Quantitative<br /> Design Researcher',
                              'loupe',
                            ],
                          ]}
                          icon
                          iconColor={'violet'}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Culture
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={['Quality and scalability', 'Agility']}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Specific attributes
                        </SectionHeader>
                        <UnorderedList
                          containerClass={'accordion__description'}
                          additionalClass={'text--light text--black'}
                          items={['Technical knowledge is a priority.']}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className={'no-gap'}>
                    <Col sm={12} md={6}>
                      <div className={'accordion__inner'}>
                        <SectionHeader
                          size={'small'}
                          additionalClass={'accordion__title'}
                          headingType={4}
                        >
                          Approach
                        </SectionHeader>
                        <Paragraph
                          size={'medium-plus'}
                          additionalClass={
                            'text--blue-light accordion__description'
                          }
                        >
                          Incremental, high-impact wins over short-term goals.
                        </Paragraph>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
              <AbsoluteCTA
                additionalClass={'accordion__CTA'}
                id={'page-development-teams-solution'}
              />
              <FullwidthImage
                image={data.scaleTeamImage.childImageSharp.fluid}
                alt={''}
              />
            </>
          </AccordionCustom>
        </Accordion>
      </>
    )}
  />
);

export default Teams;
